import { IconSearch, IconSend } from '@medflex/components/stories/Icons';
import InputField from '@medflex/components/stories/InputField';
import Modal from '@medflex/components/stories/Modal';
import useIsWideScreen from '@medflex/hooks/useIsWideScreen';
import matchesSearchTerm from '@medflex/utils/matchesSearchTerm';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import joinUmlauts from '@medflex/utils/joinUmlauts';
import ContactList from '@medflex/components/stories/ContactList';
import { useHistory } from 'react-router';
import Button from '@medflex/components/stories/Button';
import sortBy from 'lodash/sortBy';
import isSelf from '@medflex/utils/isSelf';
import Fade, { Container as FadeContainer } from '@medflex/components/stories/Fade';
import useDisableRubberBandScrolling from '@medflex/hooks/useDisableRubberBandScrolling';
import { UserPlusIcon } from '@heroicons/react/24/solid';
import SearchHighlight from '@medflex/components/stories/SearchHighlight';
import useUserProfile from '../hooks/useUserProfile';
import getSubtitle from '../../utils/getSubtitle';
import useIsModal, { MODALS } from '../hooks/useIsModal';
import useModal from '../hooks/useModal';
import useAddressBookContacts from '../hooks/useAddressBookContacts';

const ContactSearchModule = () => {
  const history = useHistory();
  const { all: contacts } = useAddressBookContacts();
  const { t } = useTranslation('messenger');
  const { isModal } = useIsModal();
  const { close, open } = useModal();
  const isWideScreen = useIsWideScreen();
  const [query, setQuery] = useState('');
  const ref = useRef();
  const rubberBandRef = useDisableRubberBandScrolling();
  const { userProfile } = useUserProfile();
  const [scrollbox, setScrollbox] = useState();

  const handleClose = () => close({ modal: MODALS.contactSearch });

  const handleChatClicked = ({ userId, isTeam }) => {
    history.push(`/address-book/${userId}${isTeam ? '?isTeamMember=true' : ''}`);
  };

  const handleInvitePatient = () => {
    open({ invitePatient: 'email', modal: null });
  };

  const handleInvitePhysician = () => {
    open({ modal: 'invitePhysician' });
  };

  const resultSet = useMemo(
    () =>
      contacts.filter(
        contact =>
          contact.id &&
          (contact.pairedAt || contact.isTeam) &&
          !isSelf(contact.user) &&
          matchesSearchTerm(query, contact.user.fullNameWithTitle),
      ),
    [contacts, query],
  );

  const resultSetByFirstLetter = useMemo(() => {
    const groupedByLetter = resultSet.reduce((memo, contact) => {
      const letter = joinUmlauts(contact.user.fullNameWithTitle.toLowerCase().charAt(0));

      return {
        ...memo,
        [letter]: [...(memo[letter] || []), contact],
      };
    }, {});

    return sortBy(Object.entries(groupedByLetter), ([key]) => key);
  }, [resultSet]);

  useEffect(() => {
    if (ref && isModal && isWideScreen) {
      ref.current.focus();
    }
  }, [ref, isModal, isWideScreen]);

  return (
    <Modal
      showCloseButton
      onClickCloseButton={handleClose}
      onClickOutside={handleClose}
      title={t('ContactSearchModule.title')}
    >
      <div data-component="ContactSearchModule">
        <div>
          <div className="flex mt-2 flex-grow-0 flex-shrink-0 mb-4">
            <InputField
              placeholder={t('ContactsSelect.search.placeholder')}
              className="flex-shrink-0 flex-grow mr-2"
              onChange={event => {
                setQuery(event.target.value);
              }}
              value={query}
              innerRef={ref}
              afterInput={
                <div className="pr-4 flex justiy-center items-center cursor-pointer">
                  <IconSearch size="5" className="fill-current text-slate-500" />
                </div>
              }
            />
          </div>

          <FadeContainer scrollbox={scrollbox}>
            {({ scrolledToEnd }) => (
              <>
                <div
                  className="p-2 overflow-auto max-h-64"
                  ref={node => {
                    setScrollbox(node);
                    rubberBandRef.current = node;
                  }}
                >
                  {resultSetByFirstLetter.map(([letter, group]) => (
                    <ContactList key={letter}>
                      <div className="font-semibold">{letter.toUpperCase()}</div>
                      {group.map(contact => {
                        const { id, user, pairedAt, isTeam } = contact;
                        return (
                          <ContactList.Item
                            enabled={typeof pairedAt !== 'undefined' || isTeam}
                            avatarSize="8"
                            avatarSrc={user.avatar}
                            subtitle={getSubtitle(contact, userProfile, t)}
                            avatarInitials={user.initials}
                            after={<IconSend className="text-blue-500" />}
                            key={id}
                            onClick={() => handleChatClicked({ userId: user.id, isTeam })}
                            title={
                              <SearchHighlight text={user.fullNameWithTitle} toHighlight={query} />
                            }
                          />
                        );
                      })}
                    </ContactList>
                  ))}
                </div>
                <Fade hidden={scrolledToEnd} />
              </>
            )}
          </FadeContainer>
        </div>
        <div className="border-t-2 border-slate-200 my-6" />
        <div className="p-2">
          <Button kind="link" onClick={handleInvitePatient}>
            <UserPlusIcon className="w-text h-text" />
            <span className="ml-2">{t('ContactSearchModule.invitePatient')}</span>
          </Button>
          <Button className="ml-4" kind="link" onClick={handleInvitePhysician}>
            <UserPlusIcon className="w-text h-text" />
            <span className="ml-2">{t('ContactSearchModule.invitePhysician')}</span>
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ContactSearchModule;
